/**
 * The sidebar's time tracking row, above Notifications: a stopwatch with a
 * dot for the tracker's state (red while a timer runs, pulsing while a case
 * is counting, grey when idle on a case, none when paused), then the case,
 * an amber dot for unreleased time and the elapsed time. The popover pauses
 * or switches the case, links to the timesheet, warns about an earlier
 * unreleased day, and explains capture the first time.
 */
import {
  AlertTriangle,
  Bell,
  Clock,
  Lock,
  Pause,
  Play,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Square,
  Timer,
  X
} from "lucide-react";
import { useEffect, useRef, useState, type FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { SettingsModal } from "@/components/settings/SettingsModal";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { timeActions } from "../actions";
import { TimeAPI } from "../api/timeApi";
import { CaseDot, CasePicker } from "../components/CasePicker";
import { useTimerElapsed } from "../components/TimerButton";
import { refreshTime, useTimeToday } from "../queries";
import { timePathFor } from "../routes";
import { nudgeLine } from "../home/homeModel";
import { formatDuration, formatHours } from "../utils";
import { setTrackingPaused } from "./pause";
import { labelFor, useTrackerStore } from "./trackerStore";

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const carryLine = (
  days: { weekday: string; count: number }[]
): string | null => {
  if (!days.length) return null;
  const count = days.reduce((n, d) => n + d.count, 0);
  const oldest = days[0].weekday;
  return days.length === 1
    ? `${oldest} has ${count} unreleased ${plural(count, "entry", "entries")}`
    : `${oldest} and ${days.length - 1} later ${plural(
        days.length - 1,
        "day",
        "days"
      )} have ${count} unreleased entries`;
};

export const TrackerChip: FC = () => {
  const enabled = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectTimeTrackingEnabled(s)
  );
  const navigate = useNavigate();
  const today = useTimeToday(enabled);
  const context = useTrackerStore((s) => s.context);
  const seconds = useTrackerStore((s) => s.seconds);
  const counting = useTrackerStore((s) => s.counting);
  const paused = useTrackerStore((s) => s.paused);
  const labels = useTrackerStore((s) => s.labels);
  const [open, setOpen] = useState(false);
  const [firstRunDone, setFirstRunDone] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data } = today;
  const timer = data?.timer ?? null;
  const timerSeconds = useTimerElapsed(timer);
  const hydrated = useRef(false);
  useEffect(() => {
    if (!data || hydrated.current) return;
    hydrated.current = true;
    useTrackerStore.getState().setPaused(data.paused);
  }, [data]);

  const firstRun = Boolean(data && !data.first_run_seen && !firstRunDone);
  useEffect(() => {
    if (firstRun) setOpen(true);
  }, [firstRun]);

  if (!enabled) return null;

  const caseId = context?.individual_id ?? context?.foreign_national_id ?? null;
  const label = labelFor(labels, context) ?? context?.title ?? null;
  const onCase = Boolean(caseId);
  const timerCase = timer?.individual_id
    ? labels[timer.individual_id] ?? null
    : null;
  const carry = carryLine(data?.earlier_unreleased ?? []);
  const nudge = nudgeLine(data?.nudge);

  const finishFirstRun = () => {
    if (!firstRun) return;
    setFirstRunDone(true);
    void TimeAPI.saveMySettings({ first_run_seen: true }).then(
      refreshTime,
      () => undefined
    );
  };

  const goTimesheet = () => {
    setOpen(false);
    finishFirstRun();
    navigate(timePathFor());
  };

  const text = timer
    ? timerCase ?? "Timer"
    : paused
    ? "Tracking Paused"
    : label ?? (onCase ? "This Case" : "No Case");
  const elapsed = timer
    ? formatDuration(timerSeconds)
    : onCase && !paused
    ? formatDuration(seconds)
    : null;
  const dot = timer
    ? "animate-pulse bg-danger"
    : paused
    ? null
    : onCase && counting
    ? "animate-pulse bg-primary"
    : onCase
    ? "bg-fg-4"
    : null;
  const unreleased = Boolean(carry || nudge);
  const tip = [text, elapsed, unreleased ? "Unreleased time" : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <SidebarMenuItem>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) finishFirstRun();
        }}
      >
        <SidebarMenuButton asChild isActive={open} tooltip={tip}>
          <PopoverTrigger
            data-testid="tracker-chip"
            aria-label={`Time tracking: ${tip}`}
          >
            <span className="relative inline-flex shrink-0">
              <Timer className="size-4" />
              {dot ? (
                <span
                  aria-hidden
                  data-testid="tracker-dot"
                  className={cn(
                    "absolute -top-0.5 -right-0.5 size-2 rounded-full ring-1 ring-sidebar",
                    dot
                  )}
                />
              ) : null}
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                (paused || (!onCase && !timer)) && "text-fg-3"
              )}
            >
              {text}
            </span>
            {unreleased ? (
              <span
                aria-hidden
                data-testid="tracker-unreleased"
                className="size-1.5 shrink-0 rounded-full bg-warn"
              />
            ) : null}
            {elapsed ? (
              <span className="shrink-0 text-sm text-fg-3 tabular-nums">
                {elapsed}
              </span>
            ) : null}
          </PopoverTrigger>
        </SidebarMenuButton>
        <PopoverContent
          side="right"
          align="end"
          className="w-85 overflow-hidden p-0"
          data-testid="tracker-popover"
        >
          {firstRun ? (
            <div
              className="flex flex-col gap-2 border-b border-line-2 bg-fill-1 p-3.5"
              data-testid="tracker-first-run"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="size-4 text-primary" />
                CaseBlink now drafts your time
              </span>
              <p className="text-sm leading-4.5 text-fg-3">
                While a case is open and you're active, the minutes count toward
                that case. Each evening you get suggested entries to review.
              </p>
              <ul className="flex flex-col gap-1 text-sm leading-4.5">
                <li className="flex gap-1.5">
                  <Lock className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                  Only you see what's captured until you release it.
                </li>
                <li className="flex gap-1.5">
                  <Clock className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                  Idle time over 3 minutes is never counted.
                </li>
                <li className="flex gap-1.5">
                  <Sparkles className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                  Agent run time is context only, never billed.
                </li>
              </ul>
              <span className="mt-1 flex gap-1.5">
                <Button variant="primary" size="xs" onClick={finishFirstRun}>
                  Got It
                </Button>
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => {
                    finishFirstRun();
                    setOpen(false);
                    setSettingsOpen(true);
                  }}
                >
                  Capture settings
                </Button>
              </span>
            </div>
          ) : null}
          {timer ? (
            <div
              className="flex items-center gap-2 border-b border-line-2 px-3 py-2.5"
              data-testid="tracker-timer"
            >
              <Square className="size-3 shrink-0 fill-danger text-danger" />
              <span className="min-w-0 flex-1 truncate text-base font-medium">
                {timerCase ? `Timer · ${timerCase}` : "Timer running"}
              </span>
              <span className="text-base font-medium tabular-nums">
                {formatDuration(timerSeconds)}
              </span>
              <Button
                variant="primary"
                size="xs"
                onClick={() => void timeActions.stopTimer()}
              >
                Stop
              </Button>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label="Discard the timer"
                onClick={() => void timeActions.stopTimer(true)}
              >
                <X />
              </Button>
            </div>
          ) : null}
          <div className="flex flex-col gap-2.5 p-3">
            <span className="text-sm text-fg-3">
              {paused ? "Paused" : "Now tracking"}
            </span>
            <span className="flex items-center gap-2">
              {onCase ? (
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
                  <CaseDot id={caseId} />
                  <span className="truncate">{label ?? "This case"}</span>
                </span>
              ) : (
                <span className="text-base text-fg-3">
                  Not on a case. Time isn't counted.
                </span>
              )}
              <span className="flex-1" />
              {onCase ? (
                <span className="text-base font-medium tabular-nums">
                  {formatDuration(seconds)}
                </span>
              ) : null}
            </span>
            <p className="text-sm leading-4 text-fg-3">
              Follows the case you have open. Switch if you're working on
              something else, like a phone call.
            </p>
            <span className="flex flex-wrap gap-1.5">
              <Button
                size="xs"
                variant="secondary"
                onClick={() => setTrackingPaused(!paused)}
                data-testid="tracker-pause"
              >
                {paused ? <Play /> : <Pause />}
                {paused ? "Resume" : "Pause"}
              </Button>
              <CasePicker
                value={context?.individual_id ?? null}
                onPick={(option) => {
                  useTrackerStore
                    .getState()
                    .addLabels({ [option.individual_id]: option.label });
                  useTrackerStore.getState().setOverride({
                    individual_id: option.individual_id,
                    foreign_national_id: option.foreign_national_id ?? null,
                    surface: "switched",
                    artifact: null,
                    title: option.label
                  });
                }}
                trigger={
                  <Button
                    size="xs"
                    variant="secondary"
                    data-testid="tracker-switch"
                  >
                    <RefreshCw />
                    Switch case
                  </Button>
                }
              />
            </span>
          </div>
          {nudge ? (
            <div
              className="flex items-center gap-2 border-t border-line-2 bg-warn-soft px-3 py-2"
              data-testid="tracker-nudge"
            >
              <Bell className="size-3.5 shrink-0 text-warn" />
              <span className="flex-1 text-sm">{nudge}</span>
              <Button
                size="xs"
                variant="link"
                className="h-auto px-1"
                onClick={() => goTimesheet()}
              >
                Release
              </Button>
            </div>
          ) : null}
          {carry ? (
            <div
              className="flex items-center gap-2 border-t border-line-2 bg-warn-soft px-3 py-2"
              data-testid="tracker-carry"
            >
              <AlertTriangle className="size-3.5 shrink-0 text-warn" />
              <span className="flex-1 text-sm">{carry}</span>
              <Button
                size="xs"
                variant="link"
                className="h-auto px-1"
                onClick={() => goTimesheet()}
              >
                Review
              </Button>
            </div>
          ) : null}
          <div className="flex items-center gap-2 border-t border-line-2 px-3 py-2">
            <span className="text-sm text-fg-3">
              Today{" "}
              <span className="font-medium text-fg tabular-nums">
                {formatHours(data?.totals.hours ?? 0)}
              </span>{" "}
              captured
            </span>
            <span className="flex-1" />
            <Button
              size="xs"
              variant="link"
              className="h-auto px-1"
              onClick={() => goTimesheet()}
            >
              Open timesheet
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {settingsOpen ? (
        <SettingsModal
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          initialSection="time-capture"
        />
      ) : null}
    </SidebarMenuItem>
  );
};
